import { useEffect, useRef } from 'react';
import { Color, type Move } from '@draughts/shared';
import { useGameStore } from '../store/game-store';
import { useBlindStore } from '../store/blind-store';

/**
 * Chi ha giocato l'ultima mossa, dato il turno CORRENTE.
 *
 * Quando una mossa entra in cronologia il turno e' GIA' ruotato, quindi ha mosso l'altro.
 * Sta qui, in un posto solo, perche' lo chiedono due componenti diversi — il banner
 * dell'annuncio e il blocco dell'ultima mossa nella barra — e due pallini che si
 * contraddicono sarebbero peggio di nessun pallino.
 */
export function moverOf(currentTurn: Color): 'white' | 'black' {
  return currentTurn === Color.WHITE ? 'black' : 'white';
}

export interface BlindMoveAnnouncement {
  /** La mossa da annunciare, o `null` se non c'è niente da dire. */
  move: Move | null;
  /** La finestra dei cinque secondi è aperta. */
  visible: boolean;
}

/**
 * L'ANNUNCIO DELL'ULTIMA MOSSA nella modalità alla cieca.
 *
 * Bendati non si vede niente, quindi non c'è modo di sapere cosa ha giocato l'avversario:
 * nel gioco alla cieca vero la mossa viene DETTA ad alta voce, ed è quella l'unica
 * informazione che il giocatore riceve. Qui la dicono, per cinque secondi, l'alone sulle
 * caselle e il banner con la notazione — che leggono tutti e due da qui, così non possono
 * raccontare due cose diverse.
 *
 * Si annunciano anche le PROPRIE mosse, e non è ridondanza: bendati, toccare partenza e
 * arrivo non produce nessun segno a schermo, quindi senza l'annuncio non si saprebbe
 * nemmeno se la propria mossa è stata accettata.
 *
 * Legge lo store di gioco e non le prop dello schermo perché così vale nello stesso modo
 * per la partita locale e per quella online: l'online riversa la board del server dentro
 * questo store con `syncFromExternal`, che scrive sia `moveHistory` sia `variant`. Vale
 * quindi anche per arena e torneo.
 */
export function useBlindMoveAnnouncement(active: boolean): BlindMoveAnnouncement {
  const moveHistory = useGameStore((s) => s.moveHistory);
  const openingPlyCount = useGameStore((s) => s.openingPlyCount);
  const reviewIndex = useGameStore((s) => s.reviewIndex);
  const announcedUntilMs = useBlindStore((s) => s.announcedUntilMs);
  const announceMove = useBlindStore((s) => s.announceMove);
  const endAnnounce = useBlindStore((s) => s.endAnnounce);

  const moveCount = moveHistory.length;
  // Parte dalla lunghezza attuale: una partita ripresa a metà (o appena aperta con
  // un'apertura già applicata) non annuncia niente finché non si muove davvero.
  const prevCountRef = useRef(moveCount);

  useEffect(() => {
    const prev = prevCountRef.current;
    prevCountRef.current = moveCount;
    if (!active) return;

    // Il fronte è la CRESCITA del conto mosse, non il cambio di turno: anche un
    // rifai-mossa gira il turno, e un annuncio legato al turno scatterebbe a ogni
    // annullamento. Una diminuzione (rifai-mossa) non annuncia niente.
    //
    // E deve crescere di ESATTAMENTE 1. Una presa multipla resta una `Move` sola, quindi
    // +1 vuol sempre dire «è stata giocata una mossa»; un salto più largo è una
    // risincronizzazione — il rientro dopo una disconnessione, o l'apertura sorteggiata
    // applicata in blocco — e annunciarlo spaccerebbe per appena giocata una mossa vecchia.
    if (moveCount !== prev + 1) return;

    // Le semi-mosse dell'apertura sorteggiata non le ha giocate nessuno, e hanno già il
    // loro banner. Il ref da solo non basta: su una rivincita il componente NON si
    // rimonta e l'apertura arriva a caldo, che con `random1` è proprio una crescita di 1.
    if (moveCount <= openingPlyCount) return;

    // In revisione l'ultima mossa mostrata è quella RIVISTA: accendere l'alone lì
    // scoprirebbe una posizione che il giocatore sta cercando di tenere a mente.
    if (reviewIndex !== null) return;

    announceMove();
  }, [active, moveCount, openingPlyCount, reviewIndex, announceMove]);

  // Il timer si arma sulla SCADENZA e non sulla durata: una seconda mossa dentro la
  // finestra sposta `announcedUntilMs`, questo effetto si riarma sul nuovo istante, e la
  // finestra riparte intera invece di chiudersi al termine della prima.
  useEffect(() => {
    if (announcedUntilMs === null) return;
    const remaining = announcedUntilMs - Date.now();
    if (remaining <= 0) {
      endAnnounce();
      return;
    }
    const timer = setTimeout(endAnnounce, remaining);
    return () => clearTimeout(timer);
  }, [announcedUntilMs, endAnnounce]);

  const visible = active && announcedUntilMs !== null;
  return {
    move: visible ? moveHistory[moveCount - 1] ?? null : null,
    visible,
  };
}
